import {
  ItineraryEvent,
  ItineraryVersion,
  ZoneGeometryDto,
} from 'core/graphql/types';
import useAppSelector from 'core/hooks/useAppSelector';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { Layer, MapLayerMouseEvent, Marker, Popup, Source } from 'react-map-gl';
import PulsatingDot from 'components/pulsatingDot/PulsatingDot';
import { CourierPosition } from 'core/grpc/trafficUpdates_pb';
import DesignButton from 'components/button/DesignButton';
import {
  selectShowCourierRoute,
  selectShowDeliveriesRoute, selectShowMicrozones,
  selectShowStopRoute,
  selectZoomLevel,
} from 'pages/itinerary/MapSlice';

import {
  selectClickedDeliveryId,
  selectHoveredDeliveryId,
} from 'pages/itinerary/ItinerarySlice';
import useItineraryPolyline from 'core/hooks/useItineraryPolyline';
import MapBoxMap from 'components/mapboxmap/MapBoxMap';
import useEventsPolyline from 'core/hooks/useEventsPolyline';
import useLiveTrackingPolyline from 'core/hooks/useLiveTrackingPolyline';
import MarkerContent from './components/MarkerContent';

import {
  courierLineLayer,
  deliveriesLineLayer,
  stopsLineLayer,
} from './components/polylines/LineLayer';
import {
  arrowDeliveriesLayer,
  arrowStopsLayer,
  courierLayer,
} from './components/polylines/ArrowLayer';
import InfoPanel from './components/InfoPanel';
import PopupContent, { PopupContentProps } from './components/PopupContent';
import { transformZonesToFeatureCollection } from '../../core/utils/GeoJsonUtils';

export interface MapSectionProps {
  itinerary: ItineraryVersion | undefined;
  events: ItineraryEvent[];
  liveTrackingCoordinates: CourierPosition[];
  agencyZones: ZoneGeometryDto[];
}

const MapSection = ({
  itinerary,
  events,
  liveTrackingCoordinates,
  agencyZones,
}: MapSectionProps) => {
  const selectedDelivery = useAppSelector(selectClickedDeliveryId);
  const hoveredDelivery = useAppSelector(selectHoveredDeliveryId);
  const mapZoomLevel = useAppSelector(selectZoomLevel);
  const [popupInfo, setPopupInfo] = useState<PopupContentProps | null>(null);
  const showDeliveriesRoute = useAppSelector(selectShowDeliveriesRoute);
  const showStopsRoute = useAppSelector(selectShowStopRoute);
  const showCourierRoute = useAppSelector(selectShowCourierRoute);
  const showMicrozones = useAppSelector(selectShowMicrozones);
  const [infoPanel, toggleInfoPanel] = useState(false);

  const { itineraryPolyline } = useItineraryPolyline(itinerary);
  const { eventsPolyline } = useEventsPolyline(events);
  const { liveTrackingPolyline, lastKnownLiveTrackingPosition } =
    useLiveTrackingPolyline(liveTrackingCoordinates);

  const markers = useMemo(
    () =>
      itinerary?.itineraryStops
        .filter((stop) => stop.latitude && stop.longitude)
        .map((stop, order) => {
          const stopEvents = events.filter(
            (event) =>
              stop.identifiers?.split(',').includes(`${event.identifier}`),
          );
          const shouldHighlight =
            stopEvents.map((d) => d.id).indexOf(selectedDelivery, 0) >= 0 ||
            stopEvents.map((d) => d.id).indexOf(hoveredDelivery, 0) >= 0;

          return (
            <Marker
              key={`marker-${order + 1}`}
              longitude={stop.longitude!}
              latitude={stop.latitude!}
              anchor="bottom"
              onClick={(e) => {
                e.originalEvent.stopPropagation();
                setPopupInfo({
                  itineraryStop: stop,
                  order: order + 1,
                  events: stopEvents,
                });
              }}
            >
              <div className={shouldHighlight ? 'shadow-highlight' : ''}>
                <MarkerContent
                  itineraryStop={stop}
                  order={order + 1}
                  events={stopEvents}
                />
              </div>
            </Marker>
          );
        }),
    [itinerary, events, hoveredDelivery, selectedDelivery],
  );

  const startStopMakers = useMemo(() => {
    const markerArray: ReactNode[] = [];
    if (
      itinerary?.startLatitude === itinerary?.endLatitude &&
      itinerary?.startLongitude === itinerary?.endLongitude &&
      itinerary?.startLatitude &&
      itinerary?.startLongitude
    ) {
      markerArray.push(
        <Marker
          key="marker-start-stop"
          longitude={itinerary?.startLongitude}
          latitude={itinerary?.startLatitude}
          anchor="center"
        >
          <div className="rounded-lg bg-light-black p-1">Start/Stop</div>
        </Marker>,
      );
    } else {
      if (itinerary?.startLatitude && itinerary?.startLongitude) {
        markerArray.push(
          <Marker
            key="marker-start"
            longitude={itinerary?.startLongitude}
            latitude={itinerary?.startLatitude}
            anchor="center"
          >
            <div className="rounded-lg bg-light-black p-1">Start</div>
          </Marker>,
        );
      }

      if (itinerary?.endLatitude && itinerary?.endLongitude) {
        markerArray.push(
          <Marker
            key="marker-stop"
            longitude={itinerary?.endLongitude}
            latitude={itinerary?.endLatitude}
            anchor="center"
          >
            <div className="rounded-lg bg-light-black p-1">Stop</div>
          </Marker>,
        );
      }
    }
    return markerArray;
  }, [
    itinerary?.endLatitude,
    itinerary?.endLongitude,
    itinerary?.startLatitude,
    itinerary?.startLongitude,
  ]);

  const [zonePopupInfo, setZonePopupInfo] = useState<{
    lng: number;
    lat: number;
    properties: GeoJSON.GeoJsonProperties | null;
  } | null>(null);

  const onMapMouseMove = useCallback((event: MapLayerMouseEvent) => {
    const { features, lngLat } = event;
    const hoveredFeature =
      features && features.find((f) => f.layer.id === 'zone-layer');

    if (hoveredFeature) {
      setZonePopupInfo({
        lng: lngLat.lng,
        lat: lngLat.lat,
        properties: hoveredFeature.properties,
      });
    } else {
      setZonePopupInfo(null);
    }
  }, []);

  const handleLegendToggle = () => {
    toggleInfoPanel((prev) => !prev);
  };

  return (
    <MapBoxMap
      id="itineraryDetailsMap"
      zoomLevel={mapZoomLevel}
      onMouseMove={onMapMouseMove}
      interactiveLayerIds={['zone-layer', 'zone-border-layer']}
    >
      {showMicrozones && (
        <Source
          id="zones"
          type="geojson"
          data={transformZonesToFeatureCollection(
            agencyZones,
            itinerary?.itineraryStops ?? [],
          )}
        >
          <Layer
            id="zone-layer"
            type="fill"
            paint={{
              'fill-color': [
                'case',
                ['==', ['get', 'containsMarker'], true],
                '#00FF00',
                '#FF0000',
              ],
              'fill-opacity': 0.05,
            }}
          />
          <Layer
            id="zone-border-layer"
            type="line"
            paint={{
              'line-color': '#FF0000',
              'line-width': 0.1,
            }}
          />
        </Source>
      )}

      {zonePopupInfo && (
        <Popup
          longitude={zonePopupInfo.lng}
          latitude={zonePopupInfo.lat}
          anchor="top"
          closeButton={false}
          onClose={() => setPopupInfo(null)}
        >
          <div>
            <h3 className="text-black">{zonePopupInfo.properties?.zoneCode}</h3>
          </div>
        </Popup>
      )}
      {startStopMakers}
      {markers}
      {popupInfo && (
        <Popup
          anchor="bottom"
          latitude={Number(popupInfo.itineraryStop.latitude)}
          longitude={Number(popupInfo.itineraryStop.longitude)}
          onClose={() => setPopupInfo(null)}
          className="text-black"
          offset={30}
        >
          <PopupContent
            itineraryStop={popupInfo.itineraryStop}
            order={popupInfo.order}
            events={popupInfo.events}
          />
        </Popup>
      )}
      {showStopsRoute && (
        <Source type="geojson" data={itineraryPolyline}>
          <Layer {...stopsLineLayer} />
          <Layer {...arrowStopsLayer} />
        </Source>
      )}

      {showDeliveriesRoute && (
        <Source type="geojson" data={eventsPolyline}>
          <Layer {...deliveriesLineLayer} />
          <Layer {...arrowDeliveriesLayer} />
        </Source>
      )}

      {liveTrackingCoordinates.length > 0 && showCourierRoute && (
        <>
          <Source type="geojson" data={liveTrackingPolyline}>
            <Layer {...courierLineLayer} />
            <Layer {...courierLayer} />
          </Source>
          {lastKnownLiveTrackingPosition && (
            <Marker
              anchor="center"
              longitude={lastKnownLiveTrackingPosition.longitude}
              latitude={lastKnownLiveTrackingPosition.latitude}
            >
              <PulsatingDot title="Courier position" />
            </Marker>
          )}
        </>
      )}
      {infoPanel ? (
        <InfoPanel handlePanelClose={() => handleLegendToggle()} />
      ) : (
        <DesignButton
          className="absolute top-0 right-2 w-1/12 bg-muted-white dark:bg-design-black"
          onClickHandler={() => handleLegendToggle()}
        >
          Open legend
        </DesignButton>
      )}
    </MapBoxMap>
  );
};

export default MapSection;
